import React from "react";
import { Form, Row, Col, Input, Card, Button, Typography, Divider, Space } from "antd";
import { format } from "rut.js";
import { FileTextOutlined, UserOutlined, CarOutlined, BankOutlined } from "@ant-design/icons";
import "./CartransferPayF23Form.css";

const { Title, Text } = Typography;

const CartransferPayF23Form = ({ cartransfer, onFinish, loading }) => {
  const [totalToPay, setTotalToPay] = React.useState(0);

  // Función para calcular el total a pagar
  const calculateTotalToPay = (price) => {
    if (!price || isNaN(price)) return 0;
    const numericPrice = parseFloat(price.toString().replace(/[^\d]/g, ''));
    // El impuesto es aproximadamente 1.5% del precio de venta
    const tax = Math.round(numericPrice * 0.015);
    return tax;
  };

  // Función para formatear números con separadores de miles
  const formatNumber = (number) => {
    if (!number) return '0';
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const renderFormField = (inputName, label, code, disabled = false, span = 8) => (
    <Col span={span} className="form-field-col">
      <div className="form-field-container">
        <div className="field-label">
          <Text strong>{label}</Text>
          <Text type="secondary" className="field-code">{code}</Text>
        </div>
        <Form.Item name={inputName} className="form-field-item">
          <Input 
            size="middle" 
            disabled={disabled} 
            className="form-field-input"
            placeholder={disabled ? "Campo calculado" : `Ingrese ${label.toLowerCase()}`}
          />
        </Form.Item>
      </div>
    </Col>
  );

  const renderSection = (title, icon, children) => (
    <Card className="form-section-card" size="small">
      <div className="section-header">
        <Space>
          {icon}
          <Title level={5} className="section-title">{title}</Title>
        </Space>
      </div>
      <div className="section-content">
        {children}
      </div>
    </Card>
  );
  const [form] = Form.useForm();
  // Utilidades de normalización y obtención con fallback
  const upperNoTildes = (value) =>
    value
      ?.normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();

  const firstDefined = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "");

  const contract = cartransfer.contract || {};
  const background = cartransfer.background || {};
  const permission = cartransfer.permission || {};
  const contractPermission = contract.permission || {};
  
  // Mapeo mejorado con fallbacks robustos
  const initial = {
    // Datos del contrato
    contract: {
      type: firstDefined(contract.type, background.type, permission.type),
      make: firstDefined(contract.make, background.make, permission.make),
      model: firstDefined(contract.model, background.model, permission.model),
      year: firstDefined(contract.year, background.year, permission.year),
      inscription: firstDefined(contract.inscription, background.inscription, permission.inscription),
      motor: firstDefined(contract.motor, background.motor, permission.motor),
      chasis: firstDefined(contract.chasis, background.chasis, permission.chasis),
      vin: firstDefined(contract.vin, background.vin, permission.vin),
      serial: firstDefined(contract.serial, background.serial, permission.serial),
      price: contract.price,
      repertory: cartransfer.repertory,
    },
    // Datos del permiso - mapeo robusto con múltiples fuentes
    permission: {
      commune: upperNoTildes(
        firstDefined(
          contractPermission.commune, 
          permission.commune,
          contract.commune,
          background.commune
        )
      ),
      year: firstDefined(
        contractPermission.year, 
        permission.year
        // NO usar contract.year ni background.year como fallback para el año del permiso
      ),
      siicode: firstDefined(
        contractPermission.siicode, 
        permission.siicode
        // NO usar contract.siicode ni background.siicode como fallback para el código SII del permiso
      ),
      appraisal: firstDefined(
        contractPermission.appraisal, 
        permission.appraisal
        // NO usar contract.appraisal ni background.appraisal como fallback para la tasación del permiso
      ),
    },
    // Datos del notario - con fallback a notario por defecto
    notary: (() => {
      const fallbackFull = "13756524-2";
      const full = (cartransfer.notary?.fullRut || cartransfer.notary?.rut || fallbackFull)
        .toString()
        .replace(/\./g, '');
      const [rutNum, dv] = full.includes('-') ? full.split('-') : [full, ''];
      return {
        // L03: RUT sin puntos ni guion (solo números, sin DV)
        rut: (rutNum || '').replace(/\D/g, ''),
        // L003: dígito verificador (en mayúscula). Si no viene, usamos '2' para el notario por defecto
        dv: (dv || '2').toString().toUpperCase(),
        // IMPORTANTE: El backend espera fullRut, no rut y dv separados
        fullRut: cartransfer.notary?.fullRut || full,
        firstLastName: cartransfer.notary?.firstLastName || "CABEZAS",
        secondLastName: cartransfer.notary?.secondLastName || "CEPEDA",
        names: cartransfer.notary?.names || "JUAN CARLOS",
        address: cartransfer.notary?.address || "AVENIDA PRESIDENTE IBAÑEZ 280",
        commune: cartransfer.notary?.commune || "PUERTO MONTT",
        phone: cartransfer.notary?.phone || "722695700",
      };
    })(),
    // Datos de comprador y vendedor
    buyer: {
      rut: cartransfer.buyer?.rut ? format(cartransfer.buyer.rut) : "",
      firstLastName: cartransfer.buyer?.firstLastName || "",
      secondLastName: cartransfer.buyer?.secondLastName || "",
      name: cartransfer.buyer?.name || "",
      names: cartransfer.buyer?.name || "",
      address: cartransfer.buyer?.address || "",
      commune: upperNoTildes(cartransfer.buyer?.commune),
      phone: cartransfer.buyer?.phone || "",
    },
    seller: {
      rut: cartransfer.seller?.rut ? format(cartransfer.seller.rut) : "",
      firstLastName: cartransfer.seller?.firstLastName || "",
      secondLastName: cartransfer.seller?.secondLastName || "",
      name: cartransfer.seller?.name || "",
      names: cartransfer.seller?.name || "",
      address: cartransfer.seller?.address || "",
      commune: upperNoTildes(cartransfer.seller?.commune),
      phone: cartransfer.seller?.phone || "",
    },
  };
  
  // Calcular el total inicial
  React.useEffect(() => {
    const initialTotal = calculateTotalToPay(initial.contract?.price);
    setTotalToPay(initialTotal);
  }, [initial.contract?.price]);

  // Escuchar cambios en el precio de venta
  const handlePriceChange = (changedValues, allValues) => {
    if (changedValues.contract?.price !== undefined) {
      const newTotal = calculateTotalToPay(changedValues.contract.price);
      setTotalToPay(newTotal);
    }
  };
  
  return (
    <div className="f23-form-container">
      <Card className="f23-main-card">
        <div className="form-header">
          <Space size="large">
            <FileTextOutlined className="form-icon" />
            <div>
              <Title level={2} className="form-title">Formulario F23</Title>
              <Text type="secondary" className="form-subtitle">
                Certificado de Transferencia de Vehículos - Tesorería General de la República
              </Text>
            </div>
          </Space>
        </div>

        <Form
          form={form}
          initialValues={initial}
          onFinish={onFinish}
          onValuesChange={handlePriceChange}
          layout="vertical"
          className="f23-form"
        >
          {/* Sección: Datos del Notario */}
          {renderSection(
            "Datos del Notario Oficial",
            <UserOutlined className="section-icon" />,
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <div className="form-field-container">
                  <div className="field-label">
                    <Text strong>RUT Notario</Text>
                    <Text type="secondary" className="field-code">[0023]</Text>
                  </div>
                  <Row gutter={8}>
                    <Col span={18}>
                      <Form.Item name={["notary", "rut"]}>
                        <Input size="middle" disabled readOnly className="form-field-input" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name={["notary", "dv"]}>
                        <Input size="middle" disabled readOnly className="form-field-input" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Col>
              {renderFormField(["notary", "firstLastName"], "Apellido Paterno", "[0021]", false, 6)}
              {renderFormField(["notary", "secondLastName"], "Apellido Materno", "[0022]", false, 6)}
              {renderFormField(["notary", "names"], "Nombres", "[0025]", false, 6)}
              {renderFormField(["notary", "address"], "Dirección Notaría", "[0026]", false, 8)}
              {renderFormField(["notary", "commune"], "Comuna Notaría", "[0028]", false, 8)}
              {renderFormField(["notary", "phone"], "Teléfono Notaría", "[0029]", false, 8)}
            </Row>
          )}

          {/* Sección: Datos del Vehículo */}
          {renderSection(
            "Datos del Vehículo",
            <CarOutlined className="section-icon" />,
            <Row gutter={[16, 16]}>
              {renderFormField(["contract", "type"], "Tipo de Vehículo", "[0034]", false, 6)}
              {renderFormField(["contract", "make"], "Marca", "[0035]", false, 6)}
              {renderFormField(["contract", "model"], "Modelo", "[0036]", false, 6)}
              {renderFormField(["contract", "year"], "Año Fabricación", "[0037]", false, 6)}
              {renderFormField(["contract", "inscription"], "Patente Única", "[0012]", false, 6)}
              {renderFormField(["contract", "repertory"], "Repertorio Número", "[0017]", false, 6)}
              {renderFormField(["contract", "motor"], "Número de Motor", "[0050]", false, 6)}
              {renderFormField(["contract", "chasis"], "Número de Chasis", "[0051]", false, 6)}
              {renderFormField(["contract", "price"], "Precio de Venta", "[0010]", false, 6)}
            </Row>
          )}

          {/* Sección: Datos del Permiso */}
          {renderSection(
            "Datos del Permiso de Circulación",
            <FileTextOutlined className="section-icon" />,
            <Row gutter={[16, 16]}>
              {renderFormField(["permission", "commune"], "Comuna Último Permiso", "[0018]", false, 8)}
              {renderFormField(["permission", "year"], "Año Permiso", "[0019]", false, 8)}
              {renderFormField(["permission", "siicode"], "Código S.I.I.", "[0009]", false, 8)}
              {renderFormField(["permission", "appraisal"], "Avalúo Fiscal", "[0011]", false, 8)}
            </Row>
          )}

          {/* Sección: Datos del Vendedor */}
          {renderSection(
            "Datos del Vendedor",
            <UserOutlined className="section-icon" />,
            <Row gutter={[16, 16]}>
              {renderFormField(["seller", "rut"], "RUT Vendedor", "[0043]", false, 6)}
              {renderFormField(["seller", "firstLastName"], "Apellido Paterno", "[0042]", false, 6)}
              {renderFormField(["seller", "secondLastName"], "Apellido Materno", "[0044]", false, 6)}
              {renderFormField(["seller", "name"], "Nombres", "[0045]", false, 6)}
              {renderFormField(["seller", "address"], "Dirección", "[0032]", false, 8)}
              {renderFormField(["seller", "commune"], "Comuna", "[0038]", false, 8)}
              {renderFormField(["seller", "phone"], "Teléfono", "[0039]", false, 8)}
            </Row>
          )}

          {/* Sección: Datos del Comprador */}
          {renderSection(
            "Datos del Comprador",
            <UserOutlined className="section-icon" />,
            <Row gutter={[16, 16]}>
              {renderFormField(["buyer", "rut"], "RUT Comprador", "[0033]", false, 6)}
              {renderFormField(["buyer", "firstLastName"], "Apellido Paterno", "[0061]", false, 6)}
              {renderFormField(["buyer", "secondLastName"], "Apellido Materno", "[0062]", false, 6)}
              {renderFormField(["buyer", "name"], "Nombres", "[0065]", false, 6)}
              {renderFormField(["buyer", "address"], "Dirección", "[0046]", false, 8)}
              {renderFormField(["buyer", "commune"], "Comuna", "[0048]", false, 8)}
              {renderFormField(["buyer", "phone"], "Teléfono", "[0049]", false, 8)}
            </Row>
          )}

          {/* Sección: Cálculos Tributarios */}
          {renderSection(
            "Cálculos Tributarios",
            <BankOutlined className="section-icon" />,
            <Row gutter={[16, 16]}>
              {renderFormField(["contract", "price"], "Base Impuesto Municipal", "[0077]", false, 6)}
              <Col span={6}>
                <div className="form-field-container">
                  <div className="field-label">
                    <Text strong>Descuento 30% Taxis/Colectivos</Text>
                    <Text type="secondary" className="field-code">[0070]</Text>
                  </div>
                  <Form.Item className="form-field-item">
                    <Input 
                      size="middle" 
                      disabled 
                      className="form-field-input" 
                      value="0"
                      placeholder="Campo calculado" 
                    />
                  </Form.Item>
                </div>
              </Col>
              <Col span={6}>
                <div className="form-field-container">
                  <div className="field-label">
                    <Text strong>Derecho Municipal</Text>
                    <Text type="secondary" className="field-code">[0334]</Text>
                  </div>
                  <Form.Item className="form-field-item">
                    <Input 
                      size="middle" 
                      disabled 
                      className="form-field-input" 
                      value={formatNumber(totalToPay)}
                      placeholder="Campo calculado" 
                    />
                  </Form.Item>
                </div>
              </Col>
              <Col span={6}>
                <div className="form-field-container">
                  <div className="field-label">
                    <Text strong>Total a Pagar</Text>
                    <Text type="secondary" className="field-code">[0091]</Text>
                  </div>
                  <Form.Item className="form-field-item">
                    <Input 
                      size="middle" 
                      disabled 
                      className="form-field-input total-field" 
                      value={formatNumber(totalToPay)}
                      placeholder="Campo calculado"
                    />
                  </Form.Item>
                </div>
              </Col>
            </Row>
          )}

          <Divider />

          <div className="form-actions">
            <Button 
              type="primary" 
              size="large" 
              loading={loading.form} 
              htmlType="submit"
              className="submit-button"
              icon={<FileTextOutlined />}
            >
              Procesar Formulario F23
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default CartransferPayF23Form;
